import * as React from 'react';
import { deps, inject, observer, StatefulComponent } from '../../lib/component';
import { Mode } from '../../types/ui';
import { FindFiltersInput } from '../../types/graphql';
import findFilters from '../../lib/findFilters';
import { classes, style } from 'typestyle';
import getComponentStatics from '../../lib/getComponentStatics';
import { ButtonPill } from '../button-pill';
import { mediaDesktop, mediaMobileOnly, mediaTablet, mediaTabletLandscape } from '../../theme/media';
import { em, important, percent, px, rem, viewWidth } from 'csx';
import { colorSlate, colorWhite } from '../../theme/color';
import { center, horizontal, horizontallySpaced, start, startJustified, vertical, verticallySpaced } from 'csstips';
import { remToPx } from '../../lib/remToPx';
import { HeroSearchInput } from './search-input';
import { fontHeading } from '../../theme/font';
import { TextNoOrphan } from '../text-no-orphan';
import { ConnectedHeroSearchResults } from './search-results';
import { HeroSearchInputSuggestion } from './search-input-suggestions';
import { ResponsiveImage } from '../responsive-image';
import { transitionQuickEase } from '../../theme/transition';
import GradientCover from '../gradient-cover';
import { ButtonCircle } from '../button-circle';
import { SVGIcon } from '../svg-icon';

type Props = {
  i18n?: Partial<I18n>;
  styles?: Partial<Styles>;
  defaultSuggestions?: Array<HeroSearchInputSuggestion>;
  resetImpulse?: boolean;
};

type I18n = {
  splashTitle: string;
  splashContent: string;
  splashButton: string;
  question: { [key in Mode]?: string };
  dropdownLabel: { [key in Mode]?: string };
  actionButton: string;
  advancedSearch(mode: Mode, filters: FindFiltersInput): string;
};

type Styles = {
  container: string;
  background: string;
  backgroundImage: string;
  backgroundImageSlide: string;
  backgroundMatch: string;
  backgroundGradient: string;
  splash: string;
  splashTitle: string;
  splashContent: string;
  splashButton: string;
  question: string;
  questionTitle: string;
  questionFallback: string;
  questionFallbackText: string;
  questionFallbackButton: string;
  results: string;
  resultsTracks: string;
  paddle: string;
  paddleLeft: string;
  paddleRight: string;
};

type State = {
  started: boolean;
  results: boolean;
  match: HeroSearchInputSuggestion | null;
  mode: Mode;
  order: Mode[];
  query: string;
  filters: FindFiltersInput;
  backgroundIndex: number;
};

const makeState = (): State => ({
  started: false,
  results: false,
  match: null,
  mode: Mode.THEME,
  order: [Mode.THEME, Mode.ARTIST, Mode.GENRE, Mode.MOOD],
  query: '',
  filters: findFilters(),
  backgroundIndex: 0,
});

@inject(deps)
@observer
export default class HeroSearch extends StatefulComponent<Props, State> {
  private _mounted?;
  private _backgroundIterator?;

  state = makeState();

  start = () => {
    this.props.controller.analytics.sendMixpanel('User clicks get started button');
    window.location.href = 'search';
  };

  setMode = (mode: Mode) => this.setState({ results: false, mode, filters: findFilters() });

  setFilters = (match: HeroSearchInputSuggestion, query: string, filters: FindFiltersInput) => {
    if (filters.artist !== null && filters.artist !== '') {
      this.props.controller.analytics.sendMixpanel('User selects artist', {
        artist: filters.artist,
      });
    }

    if (filters.mood !== null && filters.mood !== '') {
      this.props.controller.analytics.sendMixpanel('User selects mood', {
        mood: filters.mood,
      });
    }

    if (filters.genre !== null && filters.genre !== '') {
      this.props.controller.analytics.sendMixpanel('User selects genre', {
        genre: filters.genre,
      });
    }

    if (filters.theme !== null && filters.theme !== '') {
      this.props.controller.analytics.sendMixpanel('User selects theme', {
        theme: filters.theme,
      });
    }

    this.setState({ results: true, match, query, filters });
  };

  prev = () => {
    const { mode, order } = this.state;
    const index = order.indexOf(mode);
    this.setState({ mode: order[index - 1] || order[order.length - 1] });
  };

  next = () => {
    const { mode, order } = this.state;
    const index = order.indexOf(mode);
    this.setState({ mode: order[index + 1] || order[0] });
  };

  render() {
    const { defaultSuggestions = [] } = this.props;
    const { started, results, backgroundIndex, match } = this.state;
    const { preloadContent } = this.props.controller.image;
    const { styles, i18n } = getComponentStatics<Styles, I18n>(HeroSearch, this.props);

    return (
      <div className={styles.container}>
        <div className={classes(styles.background, styles.backgroundImage)}>
          {defaultSuggestions.map((artist, index) => {
            /** we only want to load the images for the current and next slide */
            if (index - 1 > backgroundIndex) return <div key={index} />;

            return (
              <ResponsiveImage
                asBackground={true}
                identity={artist.images.identity}
                preload={preloadContent}
                className={styles.backgroundImageSlide}
                key={index.toString()}
                style={{ opacity: index === backgroundIndex ? 1 : 0 }}
              />
            );
          })}
        </div>
        <ResponsiveImage
          asBackground={true}
          preload={preloadContent}
          className={classes(styles.background, styles.backgroundMatch, styles.backgroundImageSlide)}
          identity={match ? match.images.identity : void 0}
          style={{ opacity: results && match ? 1 : 0 }}
        />
        <GradientCover className={classes(styles.background, styles.backgroundGradient)} />
        {!started && this.renderSplash(styles, i18n)}
        {started && !results && this.renderQuestion(styles, i18n)}
        {started && results && this.renderResults(styles, i18n)}
      </div>
    );
  }

  renderSplash = (styles: Styles, i18n: I18n) => (
    <div className={styles.splash}>
      <h1 className={styles.splashTitle}>{i18n.splashTitle}</h1>
      <p className={styles.splashContent}>{i18n.splashContent}</p>
      <ButtonPill className={styles.splashButton} onClick={this.start} bouncing={true} xlarge>
        {i18n.splashButton}
      </ButtonPill>
    </div>
  );

  renderQuestion = (styles: Styles, i18n: I18n) => {
    const { handleClickLink } = this.props.controller.ui;
    const { mode, filters } = this.state;
    const question = i18n.question[mode];

    return (
      <div className={styles.question}>
        <h2 className={styles.questionTitle}>
          <ButtonCircle className={classes(styles.paddle, styles.paddleLeft)} onClick={this.prev}>
            <SVGIcon.ArrowLeft />
          </ButtonCircle>
          <ButtonCircle className={classes(styles.paddle, styles.paddleRight)} onClick={this.next}>
            <SVGIcon.ArrowRight />
          </ButtonCircle>
          <TextNoOrphan value={question} />
        </h2>
        <HeroSearchInput
          key="input"
          defaultMode={mode}
          defaultFilters={filters}
          onMode={this.setMode}
          onSelect={this.setFilters}
        />
        <div className={styles.questionFallback}>
          <span className={styles.questionFallbackText}>or </span>
          <ButtonPill href="/search" onClick={handleClickLink} className={styles.questionFallbackButton}>
            <span>{i18n.advancedSearch(mode, filters)}</span>
          </ButtonPill>
        </div>
      </div>
    );
  };

  renderResults = (styles: Styles, i18n: I18n) => {
    const { filters, mode, query } = this.state;

    return (
      <div className={styles.results}>
        <HeroSearchInput
          key="input"
          defaultMode={mode}
          defaultFilters={filters}
          onMode={this.setMode}
          forceActive={true}
          onSelect={this.setFilters}
          onReset={this.setMode.bind(this, mode)}
        />
        here
        <div className={styles.resultsTracks}>
          <ConnectedHeroSearchResults mode={mode} filters={filters} query={query} />
        </div>
      </div>
    );
  };

  componentDidMount() {
    this._mounted = true;
    this._backgroundIterator = setInterval(this.iteratePlaceholder, 6000);
  }

  componentWillUnmount() {
    this._mounted = false;
    clearInterval(this._backgroundIterator);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.resetImpulse && !this.props.resetImpulse) {
      this.setState(makeState());
    }
  }

  iteratePlaceholder = () => {
    if (!this._mounted) return;

    const { defaultSuggestions } = this.props;
    const { backgroundIndex } = this.state;
    const nextIndex = defaultSuggestions && defaultSuggestions[backgroundIndex + 1] ? backgroundIndex + 1 : 0;

    this.setState({
      backgroundIndex: nextIndex,
    });
  };

  static i18n: I18n = {
    splashTitle: 'The fastest growing\nchart music library\nfor social video',
    splashContent: `License popular music quickly, easily and cheaply with Lickd.
      The music sourcing struggle is over.`,
    splashButton: 'Search Music',
    question: {
      artist: 'Which artist do you want for your video?',
      genre: 'Which style of music are you looking for?',
      mood: 'Which mood are you shooting for?',
      theme: 'What is the theme of your video?',
    },
    dropdownLabel: {
      artist: 'Artist',
      genre: 'Genre',
      mood: 'Mood',
      theme: 'Theme',
    },
    actionButton: 'Find tracks',
    advancedSearch: (mode, filters) => 'Search everything',
  };

  static styles: Styles = {
    container: style(
      {
        ...center,
        ...startJustified,
        ...horizontal,
        position: 'relative',
        height: 'calc(100vh - 10rem)',
        maxWidth: rem(64),
      },
      mediaMobileOnly({
        minHeight: rem(26),
        padding: rem(1),
      }),
      mediaTablet({
        minHeight: rem(42),
        padding: rem(5),
        maxHeight: rem(64),
      }),
      mediaTabletLandscape({
        minHeight: important('32rem'),
      }),
    ),
    background: style({
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      height: 'calc(100vh + 2rem)',
      maxHeight: rem(70),
      minHeight: rem(48),
      width: viewWidth(100),
      padding: 0,
      overflow: 'hidden',
    }),
    backgroundImage: style({
      zIndex: -3,
      backgroundColor: colorSlate.darken(0.075).toString(),
    }),

    backgroundImageSlide: style({
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      position: 'absolute',
      transition: `opacity ${transitionQuickEase}`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
    }),
    backgroundMatch: style({
      zIndex: -2,
    }),
    backgroundGradient: style({
      zIndex: -1,
    }),
    splash: style(
      {
        zIndex: 1,
        ...vertical,
        ...startJustified,
        ...start,
      },
      mediaMobileOnly({
        ...verticallySpaced(remToPx(1.25)),
      }),
      mediaTablet({
        ...verticallySpaced(remToPx(2.5)),
      }),
    ),
    splashTitle: style(
      {
        margin: 0,
        color: colorWhite.toString(),
        whiteSpace: 'pre',
        lineHeight: em(1.2),
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: viewWidth(9),
      }),
      mediaTablet({
        width: viewWidth(45),
        fontSize: rem(5),
      }),
      mediaTabletLandscape({
        fontSize: important('5vw'),
      }),
    ),
    splashContent: style(
      {
        margin: 0,
        color: colorWhite.toString(),
        lineHeight: em(2),
        fontWeight: 200,
        maxWidth: rem(50),
        whiteSpace: 'pre-line',
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: rem(1.125),
      }),
      mediaTablet({
        width: viewWidth(69),
        fontSize: rem(1.375),
      }),
    ),
    splashButton: style(
      mediaMobileOnly({
        fontSize: rem(1.25),
      }),
      mediaTablet({
        fontSize: rem(1.5),
      }),
      mediaTabletLandscape({
        fontSize: important('1.25rem'),
      }),
    ),
    question: style(
      {
        zIndex: 2,
        ...vertical,
        ...startJustified,
        ...start,
        width: percent(100),
      },
      mediaMobileOnly({
        ...verticallySpaced(remToPx(1.25)),
      }),
      mediaTablet({
        ...verticallySpaced(remToPx(2.5)),
      }),
    ),
    questionTitle: style(
      {
        position: 'relative',
        userSelect: 'none',
        margin: 0,
        width: percent(100),
        maxWidth: percent(100),
        color: colorWhite.toString(),
      },
      mediaMobileOnly({
        fontSize: viewWidth(9),
      }),
      mediaTablet({
        fontSize: rem(4),
      }),
      mediaTabletLandscape({
        fontSize: important('4vw'),
      }),
    ),
    questionFallback: style({
      ...horizontal,
      ...horizontallySpaced(10),
      ...center,
      ...startJustified,
    }),
    questionFallbackText: style(
      {
        color: colorWhite.toString(),
        fontFamily: fontHeading,
        letterSpacing: px(-1),
      },
      mediaMobileOnly({
        fontSize: px(30),
        marginBottom: px(5),
      }),
      mediaTablet({
        fontSize: px(32),
        marginBottom: px(8),
      }),
    ),
    questionFallbackButton: style({
      fontSize: px(16),
      color: important(colorWhite.toString()),
      padding: '0.6em 1em',
      $nest: {
        '& svg': {
          marginRight: px(10),
        },
      },
    }),
    results: style({
      ...vertical,
      ...startJustified,
      ...start,
      zIndex: 3,
      width: percent(100),
      height: percent(100),
    }),
    resultsTracks: style(
      {
        width: percent(100),
        flex: 1,
      },
      mediaMobileOnly({
        marginTop: rem(1.5),
      }),
      mediaTabletLandscape({
        marginTop: important(0),
      }),
      mediaTablet({
        marginTop: rem(5),
      }),
    ),
    paddle: style(
      {
        position: 'absolute',
        top: percent(50),
      },
      mediaMobileOnly({
        width: rem(1),
        height: rem(1),
        margin: '-0.675rem 0.25rem 0',
        $nest: {
          '& svg': {
            width: rem(0.5),
          },
        },
      }),
      mediaTablet({
        margin: '-1.25rem 1rem 0',
      }),
    ),
    paddleLeft: style(
      {
        left: rem(-3.5),
      },
      mediaMobileOnly({
        left: rem(-1.75),
      }),
    ),
    paddleRight: style(
      {
        right: rem(-3.5),
      },
      mediaMobileOnly({
        right: rem(-1.75),
      }),
    ),
  };
}
